import React from "react";

import { AccordionModel } from "@courses-pm2/api";

import { AccordionPanelComponent } from "@courses-pm2/accordion-component/src/ts/components/AccordionPanelComponent";

export type AccordionComponentProps = {
    /**
     * The model contains all of the content to render within each instance of
     * the component.
     */
    model: AccordionModel;
};

type AccordionComponentState = {
    /**
     * Contains the list of indexes of panels that should currently be expanded.
     */
    expandedPanels: number[];
};

/**
 * Root of the render tree for the standard image-component used to render
 * image elements hosted within PM2.
 */
export class AccordionComponent extends React.PureComponent<AccordionComponentProps, AccordionComponentState> {
    /**
     * Determine which panels are initially expanded when the component is
     * being constructed.
     */
    constructor(props: AccordionComponentProps) {
        super(props);

        // Generate list of initially expanded panels
        const expandedPanels: number[] = [];

        this.props.model.panels.forEach((panel, index) => {
            if (panel.expandInitially) {
                expandedPanels.push(index);
            }
        });

        // Set up initial state
        this.state = { expandedPanels };
    }

    /**
     * Primary render method.
     */
    public render(): React.JSX.Element | null {
        const children = this.props.model.panels.filter((it) => it.content.length > 0);

        if (children.length === 0) {
            // if no content elements were contained inside the panel, then hide it all
            // together
            return null;
        }

        return (
            <div className="accordion">
                {children.map((panel, index) => (
                    <AccordionPanelComponent
                        key={index}
                        model={this.props.model}
                        panel={panel}
                        index={index}
                        expanded={this.isPanelExpanded(index)}
                        onToggle={this.onTogglePanel}
                    />
                ))}

                <style jsx>{`
                    .accordion {
                        margin-bottom: 30px;
                    }
                `}</style>
            </div>
        );
    }

    /**
     * Listener that's triggered when the user clicks the button to toggle a
     * panel on / off.
     */
    protected onTogglePanel = (index: number) => {
        if (this.props.model.autoCollapse) {
            // If we're auto-collapsing other panels, then the only two possible
            // scenarios is that the given panel is either toggled on or all
            // panels are toggled off
            this.setState({
                expandedPanels: (
                    this.isPanelExpanded(index)
                        ? this.state.expandedPanels
                            .filter((expandedIndex) => expandedIndex !== index)
                        : [index]
                ),
            });
        } else {
            // If multiple panels are allowed to be toggled on / off, then we
            // check if we should add or remove this panel from the list of
            // expanded ones
            if (this.isPanelExpanded(index)) {
                this.setState({
                    expandedPanels: this.state.expandedPanels
                        .filter((expandedIndex) => expandedIndex !== index),
                });
            } else {
                this.setState({ expandedPanels: [...this.state.expandedPanels, index] });
            }
        }
    }

    /**
     * Helper that determines if the given panel index is currently expanded.
     */
    protected isPanelExpanded(index: number): boolean {
        return this.state.expandedPanels.find((expandedIndex) => expandedIndex === index) !== undefined;
    }
}
